import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertOctagon,
  ArrowLeft,
  Ban,
  BellRing,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  History,
  Loader2,
  MapPinned,
  PackageCheck,
  RefreshCw,
  Store,
  XCircle,
} from 'lucide-react';
import PreDispatchIntelligenceQueue from './PreDispatchIntelligenceQueue';
import RecipientIntelligenceDrawer from './RecipientIntelligenceDrawer';
import StoreSignalsInbox from './StoreSignalsInbox';
import DispatchReceptionMap from './DispatchReceptionMap';
import {
  apiErrorMessage,
  fetchOperationalIntelligenceOverview,
} from './operationalIntelligenceApi';

const WINDOWS = [
  ['30', '30 días'],
  ['90', '90 días'],
  ['180', '180 días'],
  ['365', '365 días'],
  ['all', 'Todo'],
];

const RISK_STYLES = {
  HIGH: 'border-red-200 bg-red-100 text-red-800',
  MEDIUM: 'border-amber-200 bg-amber-100 text-amber-800',
  LOW: 'border-blue-200 bg-blue-100 text-blue-800',
  NORMAL: 'border-emerald-200 bg-emerald-100 text-emerald-800',
};
const RISK_LABELS = { HIGH: 'ALTO', MEDIUM: 'MEDIO', LOW: 'BAJO', NORMAL: 'NORMAL' };

function formatDate(value) {
  if (!value) return 'Sin registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export default function OperationalIntelligenceDashboard({ setActiveComponent, initialTarget, onTargetConsumed }) {
  const [activeSurface, setActiveSurface] = useState(
    ['store-signals', 'dispatch-map'].includes(initialTarget?.surface)
      ? initialTarget.surface
      : 'pre-dispatch'
  );
  const [days, setDays] = useState('90');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedContext, setSelectedContext] = useState(null);
  const [queueRefreshSignal, setQueueRefreshSignal] = useState(0);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!initialTarget) return;
    if (initialTarget.surface === 'store-signals') setActiveSurface('store-signals');
    if (initialTarget.surface === 'dispatch-map') setActiveSurface('dispatch-map');
    onTargetConsumed?.();
  }, [initialTarget, onTargetConsumed]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchOperationalIntelligenceOverview({ days, min_attempts: 2, limit: 50 }));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo cargar la inteligencia operativa'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (activeSurface === 'historical') loadOverview();
  }, [activeSurface, loadOverview]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 4500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const summary = data?.summary || {};
  const maxReasonCount = useMemo(
    () => Math.max(...(data?.failure_reasons || []).map((item) => item.count), 1),
    [data]
  );
  const maxStatusCount = useMemo(
    () => Math.max(...(data?.status_breakdown || []).map((item) => item.count), 1),
    [data]
  );

  return (
    <div className="min-h-screen bg-slate-50 font-montserrat text-slate-900">
      <header className="bg-gradient-to-r from-brand-primary to-pink-600 text-white shadow-lg">
        <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="mb-4 flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-bold text-white/85 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
            <div>
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/15 p-3 backdrop-blur-sm">
                  <Activity className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-white/70">Fast Max</p>
                  <h1 className="text-2xl font-black tracking-tight sm:text-3xl">INTELIGENCIA OPERATIVA</h1>
                </div>
              </div>
              <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
                {activeSurface === 'pre-dispatch'
                  ? 'Revisión explicable antes de la liberación física al conductor'
                  : activeSurface === 'dispatch-map'
                    ? 'Mapa de recepcion probable para asignacion operativa asistida'
                  : activeSurface === 'store-signals'
                    ? 'Confirmaciones y solicitudes reportadas por las tiendas'
                    : 'Historial de entregas, riesgo y contexto operacional'}
              </p>
            </div>
            {activeSurface === 'historical' && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap rounded-xl bg-white/10 p-1 backdrop-blur-sm">
                  {WINDOWS.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setDays(value)}
                      className={`rounded-lg px-3 py-2 text-xs font-extrabold transition-colors ${
                        days === value ? 'bg-white text-brand-primary shadow' : 'text-white hover:bg-white/10'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={loadOverview}
                  disabled={loading}
                  className="flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-extrabold text-brand-primary shadow hover:bg-white/90 disabled:opacity-70"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
              </div>
            )}
          </div>
          <nav className="mt-5 grid w-full max-w-4xl grid-cols-1 gap-1 rounded-xl bg-white/10 p-1 backdrop-blur-sm sm:grid-cols-4" aria-label="Secciones de inteligencia operativa">
            <button
              type="button"
              onClick={() => setActiveSurface('pre-dispatch')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-extrabold transition-colors ${
                activeSurface === 'pre-dispatch' ? 'bg-white text-brand-primary shadow' : 'text-white hover:bg-white/10'
              }`}
            >
              <ClipboardCheck className="h-4 w-4" />
              PRE-DESPACHO
            </button>
            <button
              type="button"
              onClick={() => setActiveSurface('dispatch-map')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-extrabold transition-colors ${
                activeSurface === 'dispatch-map' ? 'bg-white text-brand-primary shadow' : 'text-white hover:bg-white/10'
              }`}
            >
              <MapPinned className="h-4 w-4" />
              MAPA RECEPCION
            </button>
            <button
              type="button"
              onClick={() => setActiveSurface('store-signals')}
              className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-extrabold transition-colors ${
                activeSurface === 'store-signals' ? 'bg-white text-brand-primary shadow' : 'text-white hover:bg-white/10'
              }`}
            >
              <BellRing className="h-4 w-4" />
              SEÑALES DE TIENDA
            </button>
            <button
              type="button"
              onClick={() => setActiveSurface('historical')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-extrabold transition-colors ${
                activeSurface === 'historical' ? 'bg-white text-brand-primary shadow' : 'text-white hover:bg-white/10'
              }`}
            >
              <History className="h-4 w-4" />
              HISTÓRICO
            </button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {activeSurface === 'pre-dispatch' ? (
          <PreDispatchIntelligenceQueue
            refreshSignal={queueRefreshSignal}
            onOpenContext={(item) => setSelectedContext({
              phone: item.delivery?.recipient_phone,
              preDispatchContext: item,
              source: 'pre-dispatch',
            })}
            onToast={setToast}
          />
        ) : activeSurface === 'dispatch-map' ? (
          <DispatchReceptionMap
            initialDate={initialTarget?.date}
            refreshSignal={queueRefreshSignal}
            onToast={setToast}
          />
        ) : activeSurface === 'store-signals' ? (
          <StoreSignalsInbox
            initialDate={initialTarget?.date}
            refreshSignal={queueRefreshSignal}
            onOpenContext={(item) => setSelectedContext({
              phone: item.delivery?.recipient_phone,
              source: 'store-signals',
              storeSignalContext: item,
            })}
            onToast={setToast}
          />
        ) : (
          <>
        {error && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
            <div>
              <p className="font-extrabold">No se pudo cargar el tablero</p>
              <p className="text-sm">{error}</p>
            </div>
            <button onClick={loadOverview} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white">Reintentar</button>
          </div>
        )}

        <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <SummaryCard icon={PackageCheck} label="Entregas" value={summary.total_deliveries || 0} detail={`${summary.terminal_attempts || 0} intentos terminales`} tone="slate" loading={loading && !data} />
          <SummaryCard icon={CheckCircle2} label="Éxito" value={`${summary.success_rate_pct || 0}%`} detail={`${summary.delivered || 0} entregadas`} tone="emerald" loading={loading && !data} />
          <SummaryCard icon={XCircle} label="Rechazadas" value={summary.rejected || 0} detail="Resultado terminal" tone="red" loading={loading && !data} />
          <SummaryCard icon={Ban} label="Canceladas" value={summary.cancelled || 0} detail="Resultado terminal" tone="slate" loading={loading && !data} />
          <SummaryCard icon={AlertOctagon} label="Alto riesgo" value={summary.high_risk_recipients || 0} detail={`${summary.medium_risk_recipients || 0} riesgo medio`} tone="pink" loading={loading && !data} />
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-5 py-4 sm:px-6">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-brand-primary">Investigación priorizada</p>
              <h2 className="mt-1 text-xl font-black text-slate-900">Destinatarios con mayor riesgo</h2>
              <p className="mt-1 text-sm text-slate-500">El puntaje usa solo historial de resultados. Abre una fila para revisar comunicación y señales.</p>
            </div>
            <p className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              {data?.recipient_risk?.length || 0} destinatarios
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[1100px] w-full text-left text-sm">
              <thead className="bg-slate-50 text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Riesgo</th>
                  <th className="px-4 py-3">Teléfono</th>
                  <th className="px-4 py-3">Destinatario</th>
                  <th className="px-4 py-3 text-center">Intentos</th>
                  <th className="px-4 py-3 text-center">Entregadas</th>
                  <th className="px-4 py-3 text-center">Rechazadas</th>
                  <th className="px-4 py-3 text-center">Canceladas</th>
                  <th className="px-4 py-3 text-center">Fallos %</th>
                  <th className="px-4 py-3">Último intento</th>
                  <th className="px-4 py-3">Tiendas</th>
                  <th className="w-10 px-3 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && !data ? (
                  <tr><td colSpan={11} className="px-5 py-16 text-center text-slate-500"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-brand-primary" />Consultando verdad operativa...</td></tr>
                ) : (data?.recipient_risk || []).length ? (
                  data.recipient_risk.map((recipient) => (
                    <tr
                      key={recipient.phone}
                      onClick={() => setSelectedContext({ phone: recipient.phone, source: 'historical' })}
                      className="cursor-pointer hover:bg-pink-50/40"
                    >
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-extrabold ${RISK_STYLES[recipient.risk_level] || RISK_STYLES.NORMAL}`}>
                          {RISK_LABELS[recipient.risk_level] || recipient.risk_level} · {recipient.risk_score}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 font-mono font-bold text-slate-800">{recipient.phone}</td>
                      <td className="max-w-[220px] truncate px-4 py-3.5 font-semibold text-slate-800">{recipient.recipient_name || 'Sin nombre registrado'}</td>
                      <td className="px-4 py-3.5 text-center font-bold">{recipient.terminal_attempts}</td>
                      <td className="px-4 py-3.5 text-center text-emerald-700">{recipient.delivered}</td>
                      <td className="px-4 py-3.5 text-center text-red-700">{recipient.rejected}</td>
                      <td className="px-4 py-3.5 text-center text-slate-700">{recipient.cancelled}</td>
                      <td className="px-4 py-3.5 text-center font-bold text-red-700">{recipient.failure_rate_pct}%</td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{formatDate(recipient.last_attempt_at)}</td>
                      <td className="max-w-[220px] px-4 py-3.5 text-slate-600">
                        <span className="line-clamp-2">{recipient.stores?.join(', ') || 'Sin tienda'}</span>
                      </td>
                      <td className="px-3 py-3.5"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={11} className="px-5 py-14 text-center text-slate-500">No hay destinatarios con al menos dos intentos terminales en este período.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-2">
          <VisualBars
            title="Motivos de fallo"
            subtitle="Transiciones a rechazado o cancelado"
            items={(data?.failure_reasons || []).map((item) => ({ label: item.reason, value: item.count }))}
            max={maxReasonCount}
            color="bg-gradient-to-r from-brand-primary to-pink-500"
          />
          <VisualBars
            title="Distribución de estados"
            subtitle="Salud operativa del período seleccionado"
            items={(data?.status_breakdown || []).map((item) => ({ label: item.status, value: item.count }))}
            max={maxStatusCount}
            color="bg-gradient-to-r from-blue-500 to-brand-primary"
          />
        </div>

        <SellerPerformance rows={data?.seller_performance || []} />
          </>
        )}
      </main>

      {selectedContext?.phone && (
        <RecipientIntelligenceDrawer
          phone={selectedContext.phone}
          preDispatchContext={selectedContext.preDispatchContext}
          storeSignalContext={selectedContext.storeSignalContext}
          onClose={() => setSelectedContext(null)}
          onOverviewRefresh={['pre-dispatch', 'store-signals'].includes(selectedContext.source)
            ? () => setQueueRefreshSignal((value) => value + 1)
            : loadOverview}
          onToast={setToast}
        />
      )}

      {toast && (
        <div className="fixed bottom-5 right-5 z-[15000] flex max-w-sm items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white shadow-2xl">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" />
          {toast}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, detail, tone, loading }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
    pink: 'bg-pink-100 text-brand-primary',
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className={`inline-flex rounded-xl p-2.5 ${tones[tone] || tones.slate}`}><Icon className="h-5 w-5" /></div>
      <p className="mt-3 text-xs font-extrabold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-black text-slate-900 sm:text-3xl">{loading ? '—' : value}</p>
      <p className="mt-1 text-xs text-slate-500">{detail}</p>
    </div>
  );
}

function VisualBars({ title, subtitle, items, max, color }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <h2 className="text-lg font-black text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      <div className="mt-5 space-y-3">
        {items.length ? items.slice(0, 10).map((item) => (
          <div key={item.label}>
            <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
              <span className="truncate font-semibold text-slate-700">{item.label}</span>
              <span className="font-extrabold text-slate-900">{item.value}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max((item.value / max) * 100, 3)}%` }} />
            </div>
          </div>
        )) : <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Sin datos para el período seleccionado.</p>}
      </div>
    </section>
  );
}

function SellerPerformance({ rows }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4 sm:px-6">
        <div className="rounded-xl bg-brand-primary/10 p-2.5 text-brand-primary"><Store className="h-5 w-5" /></div>
        <div>
          <h2 className="text-xl font-black text-slate-900">Rendimiento por tienda</h2>
          <p className="text-sm text-slate-500">Separa comportamiento de destinatarios de calidad de orden por tienda.</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[900px] w-full text-left text-sm">
          <thead className="bg-slate-50 text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Tienda</th>
              <th className="px-4 py-3 text-center">Intentos</th>
              <th className="px-4 py-3 text-center">Entregadas</th>
              <th className="px-4 py-3 text-center">Rechazadas</th>
              <th className="px-4 py-3 text-center">Canceladas</th>
              <th className="px-4 py-3 text-center">Éxito %</th>
              <th className="px-4 py-3 text-center">Fallos %</th>
              <th className="px-4 py-3 text-center">Destinatarios</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.user_id ?? row.seller_username} className="hover:bg-slate-50">
                <td className="px-5 py-3.5 font-bold text-slate-800">{row.seller_username}</td>
                <td className="px-4 py-3.5 text-center">{row.terminal_attempts}</td>
                <td className="px-4 py-3.5 text-center text-emerald-700">{row.delivered}</td>
                <td className="px-4 py-3.5 text-center text-red-700">{row.rejected}</td>
                <td className="px-4 py-3.5 text-center">{row.cancelled}</td>
                <td className="px-4 py-3.5 text-center font-bold text-emerald-700">{row.success_rate_pct}%</td>
                <td className="px-4 py-3.5 text-center font-bold text-red-700">{row.failure_rate_pct}%</td>
                <td className="px-4 py-3.5 text-center">{row.unique_recipients}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
